import { createRoute, lazyRouteComponent, stripSearchParams } from '@tanstack/react-router'
import type { AppointmentStatus, RegistrationStatus } from '@volee/api-client'

import { requireBackoffice } from '../auth/guard.ts'
import { numericIdParam, parseOneOf, parsePage, parsePositiveInt } from './params.ts'
import { rootRoute } from './root.tsx'

// The club card. Ids are database integers, not the PoC's UUIDs, and the page loads with
// useQuery rather than a loader so the 404 stays an in-page message.

/** The card's sections. In the URL, so a colleague can be sent straight to the campi.
 * `officials` and not `staff`: in this app staff means Volée's own people (`/staff`). */
export const CLUB_SECTIONS = ['details', 'officials', 'pitches', 'teams', 'players'] as const

export type ClubSection = (typeof CLUB_SECTIONS)[number]

export interface ClubDetailSearch {
  readonly section: ClubSection
  /** Absent means the season containing today, resolved from `GET /v1/seasons`. */
  readonly season: number | null
  /** 1-based, and in the URL for the reason every other list's page is: rule 3. */
  readonly page: number
  /** Which tesseramenti the roster shows. Released ones are out by default. */
  readonly state: RegistrationStatus
  /** Which incarichi the organigramma shows. Ended ones are out by default. */
  readonly hold: AppointmentStatus
}

export const DEFAULT_CLUB_DETAIL_SEARCH: ClubDetailSearch = {
  section: 'details',
  season: null,
  page: 1,
  state: 'active',
  hold: 'active',
}

const REGISTRATION_STATES: readonly RegistrationStatus[] = ['active', 'released', 'all']

export function parseRegistrationState(value: unknown): RegistrationStatus {
  return parseOneOf(REGISTRATION_STATES, value) ?? 'active'
}

const APPOINTMENT_STATES: readonly AppointmentStatus[] = ['active', 'ended', 'all']

export function parseAppointmentState(value: unknown): AppointmentStatus {
  return parseOneOf(APPOINTMENT_STATES, value) ?? 'active'
}

export function parseClubSection(value: unknown): ClubSection {
  return parseOneOf(CLUB_SECTIONS, value) ?? DEFAULT_CLUB_DETAIL_SEARCH.section
}

/** A club's players for one season, as a link target. */
export function clubPlayersLink(id: number, seasonYear: number | null) {
  return {
    to: '/clubs/$id',
    params: { id },
    search: { ...DEFAULT_CLUB_DETAIL_SEARCH, section: 'players', season: seasonYear },
  } as const
}

/** The card as a link target. `validateSearch` makes `section` required on every link to
 * this route, so it lives here once — the same reason `clubListLink` exists. */
export function clubCardLink(id: number) {
  return { to: '/clubs/$id', params: { id }, search: DEFAULT_CLUB_DETAIL_SEARCH } as const
}

export const clubDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/clubs/$id',
  params: numericIdParam,
  validateSearch: (search: Record<string, unknown>): ClubDetailSearch => ({
    section: parseClubSection(search.section),
    season: parsePositiveInt(search.season),
    page: parsePage(search.page),
    state: parseRegistrationState(search.state),
    hold: parseAppointmentState(search.hold),
  }),
  search: {
    // The default leaves the URL: /clubs/7, never /clubs/7?section=details.
    middlewares: [stripSearchParams(DEFAULT_CLUB_DETAIL_SEARCH)],
  },
  beforeLoad: requireBackoffice,
  component: lazyRouteComponent(() => import('../pages/club-detail.tsx')),
})
